import { Injectable } from '@angular/core';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { DashboardsActions, ReviewsActions } from '@actions/index'
import * as fromApp from '@reducers/index'
import { HttpApiService } from '@services/http-api.service';
import { RequestModel, LocationModel, ReviewModel, BusinessModel, ResellerModel, ContactQueuesModel, CampaignMetricsPerLocationModel, IncomingReviewSeriesModel, SentVsPendingModel, FinishCampaignModel } from '@models/index';


@Injectable()
export class DashboardsEffects {

    getBusinessRating$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getBusinessRating),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getSelectedBusinessProfile),
                (request: RequestModel, business: BusinessModel) => {
                    return ({
                    ...request,
                    params: request.params + `/${business.id}`
                })}
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { rating: number }) => data.rating as number),
                        map(businessRating => DashboardsActions.getBusinessRatingSuccess({ businessRating })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );

    getBusinessStarsCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getBusinessStarsCount),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getSelectedBusinessProfile),
                (request: RequestModel, business: BusinessModel) => ({
                    ...request,
                    params: request.params + `businessId=${business.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: {data: number[]}) => data.data),
                        map(businessStarsCount => DashboardsActions.getBusinessStarsCountSuccess({ businessStarsCount })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );

    getBusinessReviewCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getBusinessReviewCount),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getSelectedBusinessProfile),
                (request: RequestModel, business: BusinessModel) => ({
                    ...request,
                    params: request.params + `/${business.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { size: number }) => data.size as number),
                        map(businessReviewCount => DashboardsActions.getBusinessReviewCountSuccess({ businessReviewCount })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );

    getBusinessUserCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getBusinessUserCount),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getSelectedBusinessProfile),
                (request: RequestModel, business: BusinessModel) => ({
                    ...request,
                    params: request.params + `/${business.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { size: number }) => data.size as number),
                        map(businessUserCount => DashboardsActions.getBusinessUserCountSuccess({ businessUserCount })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );

    getBusinessContactCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getBusinessContactCount),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getSelectedBusinessProfile),
                (request: RequestModel, business: BusinessModel) => ({
                    ...request,
                    params: request.params + `/${business.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { size: number }) => data.size as number),
                        map(businessContactCount => DashboardsActions.getBusinessContactCountSuccess({ businessContactCount })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );

    getBusinessLocationCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getBusinessLocationCount),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getSelectedBusinessProfile),
                (request: RequestModel, business: BusinessModel) => ({
                    ...request,
                    params: request.params + `/${business.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { size: number }) => data.size as number),
                        map(businessLocationCount => DashboardsActions.getBusinessLocationCountSuccess({ businessLocationCount })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );

    getLastDayReviewCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getLastDayReviewCount),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `&locationId=${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.postRequest(request)
                    .pipe(
                        map((data) => data as number),
                        map(lastDayReviewCount => DashboardsActions.getLastDayReviewCountSuccess({ lastDayReviewCount })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );


    getLastWeekReviewCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getLastWeekReviewCount),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `&locationId=${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.postRequest(request)
                    .pipe(
                        map((data) => data as number),
                        map(lastWeekReviewCount => DashboardsActions.getLastWeekReviewCountSuccess({ lastWeekReviewCount })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );

    getLastMonthReviewCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getLastMonthReviewCount),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `&locationId=${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.postRequest(request)
                    .pipe(
                        map((data => data as number)),
                        map(lastMonthReviewCount => DashboardsActions.getLastMonthReviewCountSuccess({ lastMonthReviewCount })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );


    searchReviewsByLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.searchBusinessReviewsByLocation),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `&locationId=${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data) => data as { data: ReviewModel[], total: number }),
                        map(data => ReviewsActions.searchReviewsSuccess({ ...data })),
                        catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                    )
            )
        )
    );
    searchBusinessReviewsAmountByLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.searchBusinessReviewsAmountByLocation),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { size: number }) => data.size),
                        map(businessDashboardReviewsAmount => DashboardsActions.searchBusinessReviewsAmountByLocationSuccess({ businessDashboardReviewsAmount })),
                        catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                    )
            )
        )
    );
    searchBusinessFeedbacksAmountByLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.searchBusinessFeedbacksAmountByLocation),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `?locationId=${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data) => data as number),
                        map(businessDashboardFeedbacksAmount => DashboardsActions.searchBusinessFeedbacksAmountByLocationSuccess({ businessDashboardFeedbacksAmount })),
                        catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                    )
            )
        )
    );
    searchBusinessContactsAmountByLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.searchBusinessContactsAmountByLocation),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `?locationId=${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data) => data as number),
                        map(businessDashboardContactsAmount => DashboardsActions.searchBusinessContactsAmountByLocationSuccess({ businessDashboardContactsAmount })),
                        catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                    )
            )
        )
    );
    searchLocationContactsQueues$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.searchLocationContactsQueues),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `locationId=${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data) => data as ContactQueuesModel),
                        map(locationQueues => DashboardsActions.searchLocationContactsQueuesSuccess({ locationQueues })),
                        catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                    )
            )
        )
    );

    searchLocationCampaignMetrics$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.searchLocationCampaignMetrics),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data) => data as CampaignMetricsPerLocationModel),
                        map(locationCampaignMetrics => DashboardsActions.searchLocationCampaignMetricsSuccess({ locationCampaignMetrics })),
                        catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                    )
            )
        )
    );

    searchLocationTodaySeries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.searchLocationTodaySeries),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `&locationId=${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data) => data as IncomingReviewSeriesModel),
                        map(locationTodaySeries => DashboardsActions.searchLocationTodaySeriesSuccess({ locationTodaySeries })),
                        catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                    )
            )
        )
    );

    searchLocationLastWeekSeries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.searchLocationLastWeekSeries),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `&locationId=${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data) => data as IncomingReviewSeriesModel),
                        map(locationLastWeekSeries => DashboardsActions.searchLocationLastWeekSeriesSuccess({ locationLastWeekSeries })),
                        catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                    )
            )
        )
    );

    searchLocationLastMonthSeries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.searchLocationLastMonthSeries),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `&locationId=${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data) => data as IncomingReviewSeriesModel),
                        map(locationLastMonthSeries => DashboardsActions.searchLocationLastMonthSeriesSuccess({ locationLastMonthSeries })),
                        catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                    )
            )
        )
    );

    searchLocationReviewRequestTodaySeries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.searchLocationReviewRequestTodaySeries),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `&locationId=${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data) => data as IncomingReviewSeriesModel),
                        map(locationTodaySeries => DashboardsActions.searchLocationReviewRequestTodaySeriesSuccess({ locationTodaySeries })),
                        catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                    )
            )
        )
    );

    searchLocationReviewRequestLastWeekSeries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.searchLocationReviewRequestLastWeekSeries),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `&locationId=${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data) => data as IncomingReviewSeriesModel),
                        map(locationLastWeekSeries => DashboardsActions.searchLocationReviewRequestLastWeekSeriesSuccess({ locationLastWeekSeries })),
                        catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                    )
            )
        )
    );

    searchLocationReviewRequestLastMonthSeries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.searchLocationReviewRequestLastMonthSeries),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `&locationId=${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data) => data as IncomingReviewSeriesModel),
                        map(locationLastMonthSeries => DashboardsActions.searchLocationReviewRequestLastMonthSeriesSuccess({ locationLastMonthSeries })),
                        catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                    )
            )
        )
    );

    searchLocationSentVsPending$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.searchLocationSentVsPending),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data) => data as {data: SentVsPendingModel, locationId: string}),
                        map(data => DashboardsActions.searchLocationSentVsPendingSuccess({ locationSentVsPending:  data.data})),
                        catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                    )
            )
        )
    );

    searchLocationFinishCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.searchLocationFinishCampaign),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getBusinessDashboardLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: {data: FinishCampaignModel}) => data.data),
                        map(locationFinishCampaign => DashboardsActions.searchLocationFinishCampaignSuccess({ locationFinishCampaign })),
                        catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                    )
            )
        )
    );

    getResellerBusinessCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getResellerBusinessCount),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getSelectedResellerProfile),
                (request: RequestModel, reseller: ResellerModel) => ({
                    ...request,
                    params: request.params + `/${reseller.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { size: number }) => data.size as number),
                        map(resellerBusinessCount => DashboardsActions.getResellerBusinessCountSuccess({ resellerBusinessCount })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );

    getResellerUserCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getResellerUserCount),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getSelectedResellerProfile),
                (request: RequestModel, reseller: ResellerModel) => ({
                    ...request,
                    params: request.params + `/${reseller.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { size: number }) => data.size as number),
                        map(resellerUserCount => DashboardsActions.getResellerUserCountSuccess({ resellerUserCount })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );

    getResellerLocationCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getResellerLocationCount),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getSelectedResellerProfile),
                (request: RequestModel, reseller: ResellerModel) => ({
                    ...request,
                    params: request.params + `/${reseller.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { size: number }) => data.size as number),
                        map(resellerLocationCount => DashboardsActions.getResellerLocationCountSuccess({ resellerLocationCount })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );

    getSiteAdminResellerCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getSiteAdminResellerCount),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { size: number }) => data.size as number),
                        map(siteAdminResellerCount => DashboardsActions.getSiteAdminResellerCountSuccess({ siteAdminResellerCount })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );

    getSiteAdminBusinessCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getSiteAdminBusinessCount),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { size: number }) => data.size as number),
                        map(siteAdminBusinessCount => DashboardsActions.getSiteAdminBusinessCountSuccess({ siteAdminBusinessCount })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );

    getSiteAdminUserCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getSiteAdminUserCount),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { size: number }) => data.size as number),
                        map(siteAdminUserCount => DashboardsActions.getSiteAdminUserCountSuccess({ siteAdminUserCount })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );

    getSiteAdminLocationCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DashboardsActions.getSiteAdminLocationCount),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        map((data: { size: number }) => data.size as number),
                        map(siteAdminLocationCount => DashboardsActions.getSiteAdminLocationCountSuccess({ siteAdminLocationCount })),
                        catchError(error => of(DashboardsActions.dashboardsError({ error: error.message })))
                    )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private httpApiService: HttpApiService,
        private store: Store<any>
    ) { }
}